import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BRAND, SERVICE_OPTIONS } from "../lib/brand";
import { Mail, Globe, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIMES = ["9:00 AM", "10:00 AM", "11:00 AM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"];

const inputCls =
  "w-full bg-navy-700 border border-white/15 text-white placeholder:text-slate-500 px-4 py-3 outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors";
const labelCls = "block text-xs uppercase tracking-wider text-slate-400 mb-2";

export const Contact = () => {
  const [tab, setTab] = useState("book");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const [booking, setBooking] = useState({
    name: "", email: "", phone: "", company: "",
    service: SERVICE_OPTIONS[0], preferred_date: "", preferred_time: TIMES[0], notes: "",
  });
  const [contact, setContact] = useState({ name: "", email: "", company: "", message: "" });

  const submitBooking = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, booking);
      setDone(true);
      toast.success("Consultation requested", { description: "We'll confirm your slot by email shortly." });
    } catch (err) {
      toast.error("Something went wrong", { description: "Please try again or email us directly." });
    } finally {
      setLoading(false);
    }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, contact);
      setDone(true);
      toast.success("Message sent", { description: "Thank you—we'll be in touch soon." });
    } catch (err) {
      toast.error("Something went wrong", { description: "Please try again or email us directly." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 lg:py-32 bg-navy">
      <div className="mx-auto max-w-7xl px-6 md:px-12 grid lg:grid-cols-5 gap-12 lg:gap-20">
        <div className="lg:col-span-2">
          <p className="uppercase tracking-[0.2em] text-xs font-semibold text-gold mb-4">Get started</p>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-tight">
            Let's bring <span className="gold-text">clarity</span> to your next move
          </h2>
          <p className="mt-6 text-slate-400 leading-relaxed">
            Book a complimentary consultation or send us a note. Every conversation is confidential and obligation-free.
          </p>

          <div className="mt-10 space-y-5">
            <a href={`mailto:${BRAND.email}`} data-testid="contact-email" className="flex items-center gap-4 text-slate-300 hover:text-gold transition-colors">
              <Mail className="w-5 h-5 text-gold" /> {BRAND.email}
            </a>
            <div className="flex items-center gap-4 text-slate-300">
              <Globe className="w-5 h-5 text-gold" /> {BRAND.domain}
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 bg-navy-800 border border-white/10 p-8 lg:p-10 shadow-2xl shadow-black/40">
          {done ? (
            <div data-testid="form-success" className="flex flex-col items-center justify-center text-center py-16">
              <CheckCircle2 className="w-16 h-16 text-gold mb-6" />
              <h3 className="font-display text-2xl mb-2">Received.</h3>
              <p className="text-slate-400 max-w-sm">
                Thank you for reaching out to Six12 Consulting. A member of our team will respond shortly.
              </p>
              <button
                data-testid="form-reset-btn"
                onClick={() => setDone(false)}
                className="mt-8 border border-gold/60 text-gold uppercase tracking-wider text-sm px-6 py-3 hover:bg-gold hover:text-navy transition-colors"
              >
                Send another
              </button>
            </div>
          ) : (
            <>
              <div className="flex border-b border-white/10 mb-8">
                {[["book", "Book a Consultation"], ["message", "Send a Message"]].map(([k, label]) => (
                  <button
                    key={k}
                    data-testid={`tab-${k}`}
                    onClick={() => setTab(k)}
                    className={`pb-4 px-1 mr-8 text-sm uppercase tracking-wider transition-colors border-b-2 -mb-px ${
                      tab === k ? "text-gold border-gold" : "text-slate-400 border-transparent hover:text-white"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {tab === "book" ? (
                <form data-testid="booking-form" onSubmit={submitBooking} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Full name</label>
                      <input data-testid="booking-name" required className={inputCls} value={booking.name}
                        onChange={(e) => setBooking({ ...booking, name: e.target.value })} placeholder="Jane Doe" />
                    </div>
                    <div>
                      <label className={labelCls}>Email</label>
                      <input data-testid="booking-email" required type="email" className={inputCls} value={booking.email}
                        onChange={(e) => setBooking({ ...booking, email: e.target.value })} placeholder="jane@company.com" />
                    </div>
                    <div>
                      <label className={labelCls}>Phone</label>
                      <input data-testid="booking-phone" className={inputCls} value={booking.phone}
                        onChange={(e) => setBooking({ ...booking, phone: e.target.value })} placeholder="Optional" />
                    </div>
                    <div>
                      <label className={labelCls}>Company</label>
                      <input data-testid="booking-company" className={inputCls} value={booking.company}
                        onChange={(e) => setBooking({ ...booking, company: e.target.value })} placeholder="Optional" />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Service of interest</label>
                    <select data-testid="booking-service" className={inputCls} value={booking.service}
                      onChange={(e) => setBooking({ ...booking, service: e.target.value })}>
                      {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Preferred date</label>
                      <input data-testid="booking-date" required type="date" className={`${inputCls} [color-scheme:dark]`} value={booking.preferred_date}
                        onChange={(e) => setBooking({ ...booking, preferred_date: e.target.value })} />
                    </div>
                    <div>
                      <label className={labelCls}>Preferred time</label>
                      <select data-testid="booking-time" className={inputCls} value={booking.preferred_time}
                        onChange={(e) => setBooking({ ...booking, preferred_time: e.target.value })}>
                        {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>What would you like to discuss?</label>
                    <textarea data-testid="booking-notes" rows={3} className={inputCls} value={booking.notes}
                      onChange={(e) => setBooking({ ...booking, notes: e.target.value })} placeholder="A few words on your situation..." />
                  </div>
                  <button data-testid="booking-submit" type="submit" disabled={loading}
                    className="w-full flex items-center justify-center gap-2 bg-gold text-navy font-semibold uppercase tracking-wider text-sm px-8 py-4 hover:bg-gold-soft transition-colors disabled:opacity-60">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {loading ? "Submitting..." : "Request Consultation"}
                  </button>
                </form>
              ) : (
                <form data-testid="message-form" onSubmit={submitContact} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Full name</label>
                      <input data-testid="contact-name" required className={inputCls} value={contact.name}
                        onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Jane Doe" />
                    </div>
                    <div>
                      <label className={labelCls}>Email</label>
                      <input data-testid="contact-email-input" required type="email" className={inputCls} value={contact.email}
                        onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="jane@company.com" />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Company</label>
                    <input data-testid="contact-company" className={inputCls} value={contact.company}
                      onChange={(e) => setContact({ ...contact, company: e.target.value })} placeholder="Optional" />
                  </div>
                  <div>
                    <label className={labelCls}>Message</label>
                    <textarea data-testid="contact-message" required rows={5} className={inputCls} value={contact.message}
                      onChange={(e) => setContact({ ...contact, message: e.target.value })} placeholder="How can we help?" />
                  </div>
                  <button data-testid="contact-submit" type="submit" disabled={loading}
                    className="w-full flex items-center justify-center gap-2 bg-gold text-navy font-semibold uppercase tracking-wider text-sm px-8 py-4 hover:bg-gold-soft transition-colors disabled:opacity-60">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {loading ? "Sending..." : "Send Message"}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};
